import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { usePanel } from '../contexts/PanelContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import BannerStack from './BannerStack';
import MobileMenu from './MobileMenu';
import Header from './Header';
import AgentDock, { AgentCardProvider, dockColumns, useAgentCard } from './AgentDock';

const titles: Record<string, string> = {
  'connections': 'Connections',
  'users': 'Users',
  'jobs': 'Job Details',
  'files': 'File Details',
  'types': 'Document Type Details',
  'profile': 'Profile',
  'workspace': 'Workspace',
  'status': 'Status',
  'activity': 'Activity',
  'library': 'Document Library',
  'skills': 'Skills',
  'whats-new': "What's New",
};

// The agent card's actions are available to everything inside, the menus in
// the top bar included.
export default function Layout({ children }: { children: React.ReactNode }) {
  return (
    <AgentCardProvider>
      <LayoutFrame>{children}</LayoutFrame>
    </AgentCardProvider>
  );
}

function LayoutFrame({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const location = useLocation();
  const { isOpen: panelOpen, title: panelTitle, content: panelContent, closePanel } = usePanel();
  const { workspaceName, agents } = useWorkspace();
  const dock = useAgentCard();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const pathParts = location.pathname.split('/').filter(p => p);
  const activeTab = pathParts[0] || 'workspace';
  const isJobDetailPage = activeTab === 'jobs' && pathParts.length > 1;
  const isFileDetailPage = activeTab === 'files' && pathParts.length > 1;
  const isTypeDetailPage = activeTab === 'types' && pathParts.length > 1;
  const isSourceDetailPage = activeTab === 'connections' && pathParts.length > 1;

  const getTitle = () => {
    if (isJobDetailPage) {
      return 'Job Details';
    } else if (isFileDetailPage) {
      return 'File Details';
    } else if (isTypeDetailPage) {
      return 'Document Type Details';
    } else if (isSourceDetailPage) {
      return 'Connection Details';
    }
    return titles[activeTab] || (workspaceName || 'Navaris');
  };

  useEffect(() => {
    const title = getTitle();
    document.title = title ? `${title} - ${workspaceName}` : workspaceName;
  }, [activeTab, isJobDetailPage, isFileDetailPage, isTypeDetailPage, isSourceDetailPage, workspaceName]);

  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  if (!user) return null;

  const closeMobileNav = () => setMobileNavOpen(false);

  return (
    // The panel scrolls as one; the agent card is sticky so it stays in view while the content scrolls.
    <div className="h-screen grid grid-rows-[48px_minmax(0,1fr)] grid-cols-[minmax(0,1fr)]">
      <Header onMenuOpen={() => setMobileNavOpen(true)} />

      <div className="bg-offwhite overflow-y-auto min-h-0">
        <div
          className="grid items-start min-h-full transition-[grid-template-columns] duration-300 ease-in-out"
          style={{ gridTemplateColumns: agents.length > 0 ? dockColumns(dock.shown, dock.wide) : 'minmax(0,1fr)' }}
        >
          {agents.length > 0 && <AgentDock />}

          {/* data-agent-view: the agent reads this as the page. Hidden while a document is open beside the chat, so the agent reads that. */}
          <main data-agent-view={dock.shown === 'reading' ? undefined : ''} className="text-graphite min-w-0 p-3 md:p-6">
            <div className="mb-4 empty:hidden">
              <BannerStack />
            </div>
            {children}
          </main>
        </div>
      </div>

      {mobileNavOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={closeMobileNav}
          />
          <div className="absolute inset-y-0 left-0 w-72 bg-white text-graphite shadow-xl flex flex-col animate-slide-in-left">
            <div className="flex items-center justify-end px-5 h-12 shrink-0 bg-navy text-offwhite">
              <button
                onClick={closeMobileNav}
                className="text-offwhite/70 hover:text-offwhite transition-colors"
                aria-label="Close menu"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              <MobileMenu onNavigate={closeMobileNav} />
            </div>
          </div>
        </div>
      )}

      {panelOpen && (
        <div className="fixed inset-0 z-50">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={closePanel}
          />
          <div data-agent-view className="absolute inset-y-0 right-0 w-full md:w-120 bg-white shadow-xl flex flex-col animate-slide-in-right">
            <div className="p-6 border-b border-softgrey flex items-center justify-between">
              <h3 className="text-lg font-semibold">{panelTitle}</h3>
              <button onClick={closePanel} className="text-coolslate/70 hover:text-coolslate">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              <div className="prose prose-sm max-w-none space-y-6">
                {panelContent}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
